import { computed, Signal } from '@preact/signals-react';
import { EditorDeclarativeBlock } from '@/webapi/use-experience-api';
import { CompoundChange } from '@/pkg/sdk';
import { AiComponentHtmlAttribute, VideoAttributes } from '@/pkg/ai';

export function useHtmlManipulator(
  change: Signal<EditorDeclarativeBlock>,
  setHtml: (html: string) => void,
) {
  const htmlString = computed(() => {
    const block = change.value.block.value as CompoundChange;
    return block.html || ``;
  });

  const parsedHtml = computed(() => {
    const wrapper = document.createElement(`div`);
    wrapper.innerHTML = htmlString.value;
    return wrapper;
  });

  const getAttribute = (
    selector: string,
    attribute: AiComponentHtmlAttribute,
  ): string => {
    const element = parsedHtml.value.querySelector(selector);
    if (element) {
      const value = element.getAttribute(attribute.attr);
      return value !== null ? value : ``;
    }
    return ``;
  };

  const setAttribute = (
    selector: string,
    attribute: AiComponentHtmlAttribute,
    value: string,
  ) => {
    const element = parsedHtml.value.querySelector(selector);
    if (element) {
      if (value === ``) {
        element.removeAttribute(attribute.attr);
      } else {
        element.setAttribute(attribute.attr, value);
      }
      setHtml(parsedHtml.value.innerHTML);
    }
  };

  const getTextContent = (selector: string): string => {
    const element = parsedHtml.value.querySelector(selector);
    return element ? element.textContent || `` : ``;
  };

  const setTextContent = (selector: string, text: string) => {
    const element = parsedHtml.value.querySelector(selector);
    if (element) {
      element.textContent = text;
      setHtml(parsedHtml.value.innerHTML);
    }
  };

  const getHtmlContent = (selector: string): string => {
    const element = parsedHtml.value.querySelector(selector);
    return element ? element.innerHTML : ``;
  };

  const setHtmlContent = (selector: string, html: string) => {
    const element = parsedHtml.value.querySelector(selector);
    if (element) {
      element.innerHTML = html;
      setHtml(parsedHtml.value.innerHTML);
    }
  };

  const getVideoAttributes = (
    selector: string,
  ): VideoAttributes | undefined => {
    const element = parsedHtml.value.querySelector(
      selector,
    ) as HTMLVideoElement;
    if (element) {
      let source = ``;
      if (element.hasAttribute(`src`)) {
        source = element.getAttribute(`src`) || ``;
      } else {
        const sourceElement = element.querySelector(`source`);
        if (sourceElement) {
          source = sourceElement.getAttribute(`src`) || ``;
        }
      }
      return {
        poster: element?.poster,
        source,
        autoplay: element?.autoplay,
        controls: element?.controls,
        loop: element?.loop,
        muted: element?.muted,
      };
    }
    return undefined;
  };

  const setVideoAttributes = (
    selector: string,
    attrs: Partial<VideoAttributes>,
  ) => {
    const element = parsedHtml.value.querySelector(
      selector,
    ) as HTMLVideoElement;
    if (element) {
      if (attrs.poster !== undefined) {
        element.poster = attrs.poster;
      }
      if (attrs.source !== undefined) {
        if (element.hasAttribute(`src`)) {
          element.setAttribute(`src`, attrs.source);
        } else {
          let sourceElement = element.querySelector(`source`);
          if (!sourceElement) {
            sourceElement = document.createElement(`source`);
            element.appendChild(sourceElement);
          }
          sourceElement.setAttribute(`src`, attrs.source);
        }
      }
      if (attrs.autoplay !== undefined) {
        element.autoplay = attrs.autoplay;
      }
      if (attrs.controls !== undefined) {
        element.controls = attrs.controls;
      }
      if (attrs.loop !== undefined) {
        element.loop = attrs.loop;
      }
      if (attrs.muted !== undefined) {
        element.muted = attrs.muted;
      }
      setHtml(parsedHtml.value.innerHTML);
    }
  };

  const removeByGroupName = (groupName: string, fallbackSelector: string) => {
    const elements = parsedHtml.value.querySelectorAll(
      `[name="${groupName || `null`}"]`,
    );

    if (elements.length <= 0) {
      parsedHtml.value.querySelector(fallbackSelector)?.remove();
      setHtml(parsedHtml.value.innerHTML);
      return;
    }
    if (elements.length === 1) {
      elements[0].remove();
      setHtml(parsedHtml.value.innerHTML);
    } else if (fallbackSelector) {
      const fallbackElement = parsedHtml.value.querySelector(fallbackSelector);
      if (fallbackElement) {
        fallbackElement.remove();
        setHtml(parsedHtml.value.innerHTML);
      }
    }
  };

  const removeAllByGroupName = (
    groupName: string,
    fallbackSelector: string,
  ) => {
    const elements = parsedHtml.value.querySelectorAll(`[name="${groupName}"]`);
    if (elements.length <= 0) {
      const elem = parsedHtml.value.querySelector(fallbackSelector);
      if (elem && !!elem.className) {
        parsedHtml.value
          .querySelectorAll(`.${elem.className.split(` `).join(`.`)}`)
          ?.forEach((el) => el.remove());
        setHtml(parsedHtml.value.innerHTML);
      }
    } else {
      elements.forEach((el) => el.remove());
      setHtml(parsedHtml.value.innerHTML);
    }
  };

  return {
    parsedHtml,
    getAttribute,
    setAttribute,
    getTextContent,
    setTextContent,
    getHtmlContent,
    setHtmlContent,
    getVideoAttributes,
    setVideoAttributes,
    removeByGroupName,
    removeAllByGroupName,
  };
}
